import React, { useEffect, useMemo } from 'react';
import useVisitStore from '../../stores/visitStore';
import { usePermission, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * VisitDashboard — Visit overview grouped by doctor, from daily visits
 *
 * Backend: GET /api/organisation/visits/daily
 * Statuses: QUEUE, IN_PROGRESS, COMPLETED, CANCELLED
 * ═══════════════════════════════════════════════════════════════════════
 */

export default function VisitDashboard({ onBack }) {
  const canView = usePermission('VIEW_VISIT_DASHBOARD');
  const { isScoped, scopeLabel } = useScope();
  const { isDark } = useTheme();
  const { dailyVisits, loading, error, fetchDailyVisits, getDoctorGroups, getClinicSummary, clearError } = useVisitStore();

  useEffect(() => { fetchDailyVisits(); }, [fetchDailyVisits]);

  // dailyVisits drives recomputation: getDoctorGroups/getClinicSummary read it via Zustand `get()` internally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dashboard = useMemo(() => getDoctorGroups(), [dailyVisits, getDoctorGroups]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totals = useMemo(() => getClinicSummary(), [dailyVisits, getClinicSummary]);

  if (!canView) return null;

  const card = {
    borderRadius: '1rem',
    overflow: 'hidden',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
  };

  return (
    <div style={card}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #115e59 0%, #0d9488 100%)', padding: '0.875rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.125rem' }}>📊</span>
            <h2 style={{ margin: 0, color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{isScoped ? `${scopeLabel} Visit Stats` : 'Visit Dashboard'}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={() => fetchDailyVisits()}
              disabled={loading.daily}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '0.8125rem' }}
            >
              ↻ Refresh
            </button>
            {onBack && (
              <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.875rem' }}>✕</button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '0.625rem 1.25rem', background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.8125rem', color: isDark ? '#fca5a5' : '#991b1b' }}>⚠️ {error}</span>
          <button onClick={clearError} style={{ background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444', cursor: 'pointer', fontSize: '0.6875rem' }}>Dismiss</button>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
        <SummaryCell label="In Queue" value={totals.queue} icon="🔢" color="#3b82f6" isDark={isDark} />
        <SummaryCell label="In Progress" value={totals.in_progress} icon="▶" color="#0d9488" isDark={isDark} borderLeft />
        <SummaryCell label="Completed" value={totals.completed} icon="✓" color="#22c55e" isDark={isDark} borderLeft />
        <SummaryCell label="Total" value={totals.total} icon="📋" color="#0d9488" isDark={isDark} borderLeft />
      </div>

      {/* Loading / Empty */}
      {loading.daily && dashboard.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center', fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>Loading dashboard...</div>
      )}
      {!loading.daily && dashboard.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <span style={{ fontSize: '2.5rem' }}>📊</span>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', color: isDark ? '#e8e9ed' : '#374151' }}>No visit data for today</p>
        </div>
      )}

      {/* Doctor Groups */}
      <div>
        {dashboard.map((group, idx) => {
          const total = group.total || 1;
          return (
            <div
              key={group.doctor_id}
              style={{
                padding: '1rem 1.25rem',
                borderTop: idx > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  🩺 Dr. {group.doctor_name}
                </h3>
                <span style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                  {group.total} total
                </span>
              </div>

              {/* Stacked Bar */}
              <div style={{ width: '100%', height: '0.375rem', background: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6', borderRadius: '9999px', overflow: 'hidden', marginBottom: '0.625rem' }}>
                <div style={{ height: '100%', display: 'flex' }}>
                  <div style={{ width: `${((group.completed || 0) / total) * 100}%`, background: '#4ade80', transition: 'width 0.5s' }} />
                  <div style={{ width: `${((group.in_progress || 0) / total) * 100}%`, background: '#2dd4bf', transition: 'width 0.5s' }} />
                  <div style={{ width: `${((group.queue || 0) / total) * 100}%`, background: '#3b82f6', transition: 'width 0.5s' }} />
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <StatDot label="Queue" value={group.queue || 0} color="#3b82f6" isDark={isDark} />
                <StatDot label="In Progress" value={group.in_progress || 0} color="#2dd4bf" isDark={isDark} />
                <StatDot label="Done" value={group.completed || 0} color="#4ade80" isDark={isDark} />
                {(group.forwarded_out || 0) > 0 && (
                  <StatDot
                    label="↗ Sent"
                    value={group.forwarded_out}
                    color="#f59e0b"
                    isDark={isDark}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCell({ label, value, icon, color, isDark, borderLeft }) {
  return (
    <div style={{
      padding: '0.875rem 0.5rem',
      textAlign: 'center',
      borderLeft: borderLeft ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none',
    }}>
      <span style={{ fontSize: '0.875rem' }}>{icon}</span>
      <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: 700, color }}>{value}</p>
      <p style={{ margin: '0.125rem 0 0', fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
    </div>
  );
}

function StatDot({ label, value, color, isDark }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
      <span style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', background: color }} />
      <span style={{ fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>{label}</span>
      <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#e8e9ed' : '#374151' }}>{value}</span>
    </span>
  );
}
